// ***********************************************************************
// Assembly         : Veriteq.Partner.Web
// Author           : Mike Palmer
// Created          : 06-21-2024
//
// Last Modified By : Mike Palmer
// Last Modified On : 06-21-2024
// ***********************************************************************
// <copyright file="_input.js" company="NSA Group Ltd">
//     Copyright (c) NSA Group Ltd. All rights reserved.
// </copyright>
// <summary>
//     JavaScript utilities for preventing input on elements and adding border 
//     animation effects to enhance user interaction in a Blazor application.
// </summary>
// ***********************************************************************

window.input = window.input || {};

/**
 * Prevents user input on the specified input element by blocking keydown events.
 * This function disables typing into the input element by attaching an event listener
 * that prevents the default action for any key press.
 * 
 * @function preventInput
 * @memberof window.input
 * @param {string} elementId - The ID of the input element to disable.
 */
window.input.preventInput = function (elementId) {
    const input = document.getElementById(elementId);
    if (input) {
        input.addEventListener('keydown', function (event) {
            event.preventDefault();
        });
    }
};

/**
 * Restricts input to the specified allowed keys for the current event.
 * This function dynamically prevents default behavior for disallowed keypress events.
 *
 * @function restrictKeys
 * @memberof window.input
 * @param {string} elementId - The ID of the input element to restrict.
 * @param {string} allowedKeys - A string containing all allowed characters.
 */
window.input.restrictKeys = function (elementId, allowedKeys) {
    const input = document.getElementById(elementId);
    if (input) {
        const allowedSet = new Set((allowedKeys || '').toLowerCase().split(''));
        const navigationKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

        input.addEventListener('keydown', function (event) {
            const rawKey = event.key || '';
            const key = rawKey.toLowerCase();

            if (!allowedSet.has(key) && !navigationKeys.includes(rawKey)) {
                event.preventDefault();
            }
        });
    }
};

/**
 * Restricts all MudCodeInput fields to A–Z (a-z), 0–9 and forces uppercase display and value.
 */
window.input.initMudCodeInputAlphaNumUppercase = function () {
    const allowedSet = new Set('abcdefghijklmnopqrstuvwxyz0123456789'.split(''));
    const navigationKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    
    const inputs = document.querySelectorAll('input.mud-input-slot[maxlength="1"]:not([disabled]):not([type="hidden"])');
    console.log(`Found ${inputs.length} MudCodeInput fields`);

    inputs.forEach(input => {
        if (input.dataset.restrictAttached === 'true') return;
        input.dataset.restrictAttached = 'true';
        
        input.addEventListener('keydown', function (event) {
            const key = event.key.toLowerCase();
            if (!allowedSet.has(key) && !navigationKeys.includes(event.key)) {
                event.preventDefault();
            }
        });
        
        input.addEventListener('input', function () {
            window.input.recase(input, input.value.toUpperCase());
        });
    });
};

/**
 * Forces character casing on input to either upper or lower case.
 *
 * @function forceCase
 * @memberof window.input
 * @param {string} elementId - The ID of the input element.
 * @param {'upper' | 'lower'} casing - Desired casing format.
 */
window.input.forceCase = function (elementId, casing) {
    const input = document.getElementById(elementId);
    if (!input) return;

    input.addEventListener('input', function () {
        if (casing === 'upper') {
            window.input.recase(this, this.value.toUpperCase());
        } else if (casing === 'lower') {
            window.input.recase(this, this.value.toLowerCase());
        }
    });
};

/**
 * Replaces an input's value with a recased copy, keeping the caret where it was.
 * Leaves the input alone when nothing changes: writing the same value back moves the
 * caret in some inputs, and email inputs do not support selection at all.
 *
 * @function recase
 * @memberof window.input
 * @param {HTMLInputElement} input - The input to recase.
 * @param {string} cased - The value in the wanted casing.
 */
window.input.recase = function (input, cased) {
    if (cased === input.value) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    input.value = cased;
    if (start !== null && end !== null) input.setSelectionRange(start, end);
};

/**
 * Applies case transformation and character restriction rules to input elements.
 *
 * This function scans the DOM for all `<input>` elements that have either
 * a `data-case` or `data-allowed` attribute. It applies a keydown filter
 * to restrict input to the specified characters and sets up an input handler
 * to enforce consistent casing (uppercase or lowercase) as the user types.
 *
 * Expected usage:
 * - `data-case="upper"` or `data-case="lower"`: transforms input casing.
 * - `data-allowed="abc123"`: restricts allowed characters during typing.
 *
 * This should be called after all relevant inputs are rendered in the DOM.
 * It can be called directly or triggered via `observeInputs()` for dynamic rendering.
 *
 * @function enforceInputRestrictions
 * @memberof window.input
 */
/**
 * Capitalises the first letter of every word, leaving deliberate inner capitals
 * alone so McDonald and Tanayaih-Louise survive, while MICHAEL becomes Michael.
 * Mirrors NameCaseConverter on the server.
 *
 * @function toNameCase
 * @memberof window.input
 * @param {string} value - The value to normalise.
 * @returns {string} The normalised value.
 */
window.input.toNameCase = function (value) {
  if (!value) return value;
  return value.trim().replace(/[A-Za-z]+/g, function (word) {
    var shouted = word.length > 1 && word === word.toUpperCase();
    return word.charAt(0).toUpperCase() + (shouted ? word.slice(1).toLowerCase() : word.slice(1));
  });
};

/**
 * Puts an organisation name or a title into title case exactly as ToTitleCase()
 * in Veriteq.Core does: acronyms in capitals, joining words in lower case after the
 * first word, and any word typed with a capital kept as typed unless the whole
 * value is in capitals. TitleCaseScriptTests holds the two to the same answers.
 *
 * @function toTitleCase
 * @memberof window.input
 * @param {string} value - The value to normalise.
 * @returns {string} The normalised value.
 */
window.input.titleAcronyms = ['NSA', 'UK', 'USA', 'EU', 'ROI', 'NHS', 'MOD', 'SIA', 'DBS', 'BPSS', 'BS', 'BA', 'PSA', 'HR', 'IT', 'PLC', 'LLP', 'LLC', 'CIC', 'UAE', 'AI', 'DVLA', 'HMRC', 'IVA', 'CCJ', 'GPG'];

window.input.titleSmallWords = ['a', 'an', 'and', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'or', 'the', 'to', 'upon', 'with'];

window.input.titleWord = function (word, first, shouting) {
  var letters = word.replace(/[^\p{L}\p{N}]/gu, '');
  if (letters && window.input.titleAcronyms.indexOf(letters.toUpperCase()) >= 0) return word.toUpperCase();
  if (!shouting && /\p{Lu}/u.test(word)) return word;
  var lower = word.toLowerCase();
  if (!first && window.input.titleSmallWords.indexOf(lower) >= 0) return lower;
  if (word.indexOf('-') >= 0) return word.split('-').map(function (part) { return window.input.titleWord(part, true, shouting); }).join('-');
  if (word.indexOf("'") >= 0) return word.split("'").map(function (part, index) { return index === 0 || part.length > 1 ? window.input.titleWord(part, true, shouting) : part.toLowerCase(); }).join("'");
  return lower.replace(/(?<![\p{L}\p{N}])\p{L}/gu, function (start) { return start.toUpperCase(); });
};

window.input.toTitleCase = function (value) {
  if (!value || !value.trim()) return '';
  var shouting = !/\p{Ll}/u.test(value);
  return value.trim().split(' ').filter(Boolean).map(function (word, index) {
    return window.input.titleWord(word, index === 0, shouting);
  }).join(' ');
};

window.input.enforceInputRestrictions = function () {
    const inputs = document.querySelectorAll(
        'input[data-case], input[data-allowed], input[data-decimal], input.mud-input-slot[maxlength="1"]:not([disabled]):not([type="hidden"])'
    );
    
    inputs.forEach(input => {
        if (input.dataset.restrictionsAttached === 'true') return;
        input.dataset.restrictionsAttached = 'true';

        const casing = input.getAttribute('data-case');
        const allowed = input.getAttribute('data-allowed');
        const navKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

        if (casing === 'upper' || casing === 'lower') {
            input.addEventListener('input', function () {
                window.input.recase(this, casing === 'upper' ? this.value.toUpperCase() : this.value.toLowerCase());
            });
        }
        
        if (casing === 'name' || casing === 'title') {
      input.addEventListener('blur', function () {
        var cased = casing === 'title' ? window.input.toTitleCase(this.value) : window.input.toNameCase(this.value);
        if (cased === this.value) return;
        this.value = cased;
        this.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    const decimals = input.getAttribute('data-decimal');
        if (decimals !== null) {
            const limit = parseInt(decimals, 10) === 0 ? 0 : 1;
            input.addEventListener('input', function () {
                const before = this.value;
                let seen = 0;
                let cleaned = '';
                for (const character of before) {
                    if (character !== '.') { cleaned += character; continue; }
                    if (seen < limit) { cleaned += character; seen++; }
                }

                if (cleaned === before) return;
                const at = this.selectionStart - (before.length - cleaned.length);
                this.value = cleaned;
                this.setSelectionRange(at, at);
            });
        }

    if (allowed) {
            const allowedSet = new Set(allowed.toLowerCase().split(''));
            input.addEventListener('keydown', function (event) {
                const key = event.key.toLowerCase();
                if (!allowedSet.has(key) && !navKeys.includes(event.key)) {
                    event.preventDefault();
                }
            });
        }
    });
};

/**
 * Observes the DOM for dynamically inserted input elements and reapplies
 * case and character restriction logic when they appear.
 *
 * This function sets up a `MutationObserver` that watches for changes to
 * the DOM subtree and automatically re-invokes `input.enforceInputRestrictions`
 * whenever new nodes are added. This is useful for Blazor apps where inputs
 * may be rendered conditionally or via delayed components (e.g., modals, toggles).
 *
 * @function observeInputs
 * @memberof window.input
 */
window.input.observeInputs = function () {
    const observer = new MutationObserver(() => {
        window.input.enforceInputRestrictions();
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    window.input.enforceInputRestrictions();
};

/**
 * Adds a border fade animation to the specified element.
 * This function triggers a CSS animation on the element's border by toggling a CSS class,
 * creating a visual effect that can be used to draw attention to the element.
 * 
 * @function border
 * @memberof window.input
 * @param {string} elementId - The ID of the element to apply the border animation to.
 */
window.input.border = function (elementId) {
    const element = document.getElementById(elementId);
    if (element) {
        /**
         * Handles the end of the border fade animation.
         * This function removes the animation class from the element and cleans up
         * the event listener to ensure the animation can be triggered again in the future.
         * 
         * @function handleAnimationEnd
         * @memberof window.input
         * @private
         */
        const handleAnimationEnd = function () {
            element.classList.remove('border-fade-animation');
            element.removeEventListener('animationend', handleAnimationEnd);
        };

        // Reset the animation by removing and re-adding the class
        element.addEventListener('animationend', handleAnimationEnd);
        element.classList.remove('border-fade-animation');
        void element.offsetWidth; // Trigger reflow to restart the animation
        element.classList.add('border-fade-animation');
    }
};

/**
 * Attaches a global ENTER key handler to all elements with `data-enter-handler`,
 * using the element's `id` and a single .NET reference passed from Blazor.
 * Prevents Shift+Enter from triggering the action (preserves newline).
 *
 * @param {DotNetObject} dotNetRef - The .NET object to invoke the method on.
 */
window.input.autoAttachEnterHandlers = function (dotNetRef) {
    const inputs = document.querySelectorAll('[data-enter-handler]');

    inputs.forEach(el => {
        const method = el.getAttribute('data-enter-handler');
        const id = el.id;
        if (!method || !id || el.dataset.enterHandlerAttached === 'true') return;

        el.dataset.enterHandlerAttached = 'true';

        el.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                dotNetRef.invokeMethodAsync(method);
            }
        });
    });
};

/**
 * Attaches an Enter key handler to a single input element.
 * Used only when that input needs to invoke .NET from Blazor.
 *
 * @param {string} elementId - The ID of the input element.
 * @param {DotNetObject} dotNetRef - Reference to the Blazor component.
 * @param {string} methodName - The [JSInvokable] method to call.
 */
window.input.attachEnterHandler = function (elementId, dotNetRef, methodName) {
    const el = document.getElementById(elementId);
    if (!el || el.dataset.enterHandlerAttached === 'true') return;

    el.dataset.enterHandlerAttached = 'true';

    el.addEventListener('keydown', async function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            await dotNetRef.invokeMethodAsync(methodName);
        }
    });
};